import { useCallback, useRef } from 'react';
import { flushSync } from 'react-dom';

import { animationsDisabled } from 'js/src/libs/css-helpers';

const COLLAPSE_OUT_DURATION_MS = 360;
const FADE_PORTION = 0.35;

/**
 * Animates an element out (fade, then its height, padding and margins collapse
 * to nothing) before running `onDone`, so dismissing something lets the page
 * close the gap smoothly instead of jumping. Runs `onDone` immediately when
 * animations are disabled or unsupported.
 */
export default function useCollapseOut<ElementType extends HTMLElement>() {
    const ref = useRef<ElementType>(null);

    const collapseOut = useCallback((onDone: VoidFunction) => {
        const element = ref.current;
        if (!element || animationsDisabled() || typeof element.animate !== 'function') {
            onDone();
            return;
        }
        const { height, paddingTop, paddingBottom, marginTop, marginBottom } =
            getComputedStyle(element);
        const fullSize = { height, paddingTop, paddingBottom, marginTop, marginBottom };
        element.style.overflow = 'hidden';
        // Two phases on a linear timeline: fade out at full size, then close the gap.
        // Per-keyframe easing keeps each phase's own curve instead of one curve squeezing
        // the whole sequence into its first few frames.
        const animation = element.animate(
            [
                { opacity: 1, ...fullSize, easing: 'ease-out' },
                {
                    opacity: 0,
                    ...fullSize,
                    offset: FADE_PORTION,
                    easing: 'cubic-bezier(0.65, 0, 0.35, 1)',
                },
                {
                    opacity: 0,
                    height: '0px',
                    paddingTop: '0px',
                    paddingBottom: '0px',
                    marginTop: '0px',
                    marginBottom: '0px',
                },
            ],
            { duration: COLLAPSE_OUT_DURATION_MS, fill: 'forwards' },
        );
        const finish = () => {
            // Commit the caller's hide/unmount synchronously before undoing the collapse;
            // otherwise React can render it a frame later and the element flashes back at
            // full size. Undoing it means an element shown again later comes back full size.
            flushSync(onDone);
            animation.cancel();
            element.style.overflow = '';
        };
        animation.finished.then(finish, finish);
    }, []);

    return { ref, collapseOut };
}
